import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Checkbox, message, Spin } from 'antd';
import { Exchange, UpdateExchangeRequest } from '../../types/api.types';
import { useDataContext } from '../../contexts/DataContext';
import ApiService from '../../services/api';
import { MESSAGES } from '../../utils/constants';

interface EditExchangeModalProps {
  visible: boolean;
  exchange: Exchange | null;
  fiatName: string;
  onCancel: () => void;
  onSuccess: () => void;
}

const EditExchangeModal: React.FC<EditExchangeModalProps> = ({
  visible,
  exchange,
  fiatName,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [banksLoading, setBanksLoading] = useState(false);
  const [availableBanks, setAvailableBanks] = useState<string[]>([]);
  const { loadFiatSettings } = useDataContext();

  // Load banks function must be declared before useEffect
  const loadBanks = useCallback(async () => {
    if (!exchange || !fiatName) return;

    setBanksLoading(true);
    try {
      const banks = await ApiService.getBanks(fiatName, exchange.exchange.name);
      // Ensure we always set an array
      const bankNames = Array.isArray(banks) ? banks : [];
      setAvailableBanks(bankNames);
      // Pre-check existing banks by their names
      form.setFieldsValue({ banks: exchange.banks.map((bank) => bank.name) });
    } catch (error) {
      console.error('Ошибка загрузки банков:', error);
      message.error('Не удалось загрузить список банков');
    } finally {
      setBanksLoading(false);
    }
  }, [exchange, fiatName, form]);

  useEffect(() => {
    if (visible && exchange && fiatName) {
      loadBanks();
    } else {
      form.resetFields();
      setAvailableBanks([]);
    }
  }, [visible, exchange, fiatName, form, loadBanks]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const updateData: UpdateExchangeRequest = {
        banks: values.banks || [],
      };

      await ApiService.updateExchange(exchange!.id, updateData);
      
      message.success(MESSAGES.SUCCESS.EXCHANGE_UPDATED);
      
      // Обновляем данные в кэше
      await loadFiatSettings(fiatName);
      
      onSuccess();
    } catch (error) {
      console.error('Ошибка обновления биржи:', error);
      message.error(MESSAGES.ERROR.GENERIC);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setAvailableBanks([]);
    onCancel();
  };

  return (
    <Modal
      title={`Редактировать биржу: ${exchange?.exchange?.name || ''}`}
      open={visible}
      onOk={handleSubmit}
      onCancel={handleCancel}
      confirmLoading={loading}
      width="90%"
      style={{ maxWidth: 600 }}
      centered
    >
      <Spin spinning={banksLoading}>
        <Form
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          <Form.Item
            name="banks"
            label="Выберите банки"
            extra=""
          >
            <Checkbox.Group style={{ width: '100%' }} disabled={banksLoading}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: 8 }}>
                {availableBanks.map((bank) => (
                  <Checkbox key={bank} value={bank}>
                    {bank}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </Form.Item>

          {availableBanks.length === 0 && !banksLoading && (
            <div style={{ textAlign: 'center', color: '#999', padding: '20px 0' }}>
              Банки не найдены для данной биржи
            </div>
          )}
        </Form>
      </Spin>
    </Modal>
  );
};

export default EditExchangeModal;
